/**
 * WebSocket Service
 * Real-time communication for lab sessions using Socket.IO
 */
import { io } from 'socket.io-client';

/**
 * Get WebSocket URL from environment
 * Converts HTTP API URL to WebSocket URL
 */
export const getWsUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
  // Convert http:// to ws:// and https:// to wss://
  return apiUrl.replace(/^http/, 'ws').replace('/api', '');
};

class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.connectionStatus = 'disconnected';
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.statusCallbacks = [];
  }

  connect(token) {
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

    this.socket = io(API_URL, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts
    });

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.connectionStatus = 'connected';
      this.reconnectAttempts = 0;
      this.notifyStatusChange();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      this.connectionStatus = 'disconnected';
      this.notifyStatusChange();
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.connectionStatus = 'error';
      this.reconnectAttempts++;
      this.notifyStatusChange();
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('WebSocket reconnected after', attemptNumber, 'attempts');
      this.connectionStatus = 'connected';
      this.notifyStatusChange();
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('WebSocket reconnect attempt:', attemptNumber);
      this.connectionStatus = 'reconnecting';
      this.notifyStatusChange();
    });

    this.socket.on('reconnect_failed', () => {
      console.error('WebSocket reconnection failed');
      this.connectionStatus = 'failed';
      this.notifyStatusChange();
    });

    return this;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connectionStatus = 'disconnected';
      this.listeners.clear();
      this.notifyStatusChange();
    }
  }

  joinRoom(room) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('join_room', { room });
      console.log('Joined room:', room);
    }
  }

  leaveRoom(room) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('leave_room', { room });
      console.log('Left room:', room);
    }
  }

  sendMessage(event, data) {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('Cannot send message: WebSocket not connected');
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);

    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event, callback) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      const index = eventListeners.indexOf(callback);
      if (index > -1) {
        eventListeners.splice(index, 1);
      }
    }

    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  removeAllListeners(event) {
    this.listeners.delete(event);
    if (this.socket) {
      this.socket.removeAllListeners(event);
    }
  }

  onStatusChange(callback) {
    this.statusCallbacks.push(callback);
    callback(this.connectionStatus);
  }

  offStatusChange(callback) {
    const index = this.statusCallbacks.indexOf(callback);
    if (index > -1) {
      this.statusCallbacks.splice(index, 1);
    }
  }

  notifyStatusChange() {
    this.statusCallbacks.forEach(callback => callback(this.connectionStatus));
  }

  getStatus() {
    return this.connectionStatus;
  }

  isConnected() {
    return this.socket && this.socket.connected;
  }

  getId() {
    return this.socket ? this.socket.id : null;
  }
}

export default new WebSocketService();
